import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Upload.css";

const Upload = () => {
  const navigate = useNavigate();

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [uploadedBundle, setUploadedBundle] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFilesCount, setUploadedFilesCount] = useState(0);
  
  // Error bundles ke liye state
  const [errorBundles, setErrorBundles] = useState([]);
  const [loadingErrors, setLoadingErrors] = useState(false);
  const [resolvingId, setResolvingId] = useState(null); // Track which bundle is being resolved
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'reported_at', direction: 'desc' });

  useEffect(()=>{
      const login = sessionStorage.getItem("token");
      if(!login || undefined){
        alert("Login First");
        navigate("/adminP");
      }
    });

  // Fetch error bundles on component mount
  useEffect(() => {
    fetchErrorBundles();
  }, []);

  const fetchErrorBundles = async () => {
    try {
      setLoadingErrors(true);
      const response = await fetch(`${process.env.REACT_APP_ADMIN_API_URL}/error-bundles`,{
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`
            }});
      const result = await response.json();
      
      if (result.success) {
        setErrorBundles(result.data);
      } else {
        console.error("Failed to fetch error bundles:", result.message);
        setMessage("Failed to fetch error bundles");
      }
    } catch (error) {
      console.error("Error fetching error bundles:", error);
      setMessage("Error fetching error bundles");
    } finally {
      setLoadingErrors(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    if (selectedFiles.length > 200) {
      setMessage("You can only upload up to 200 files at a time");
      setFiles([]);
      e.target.value = "";
      return;
    }
    
    const pdfFiles = selectedFiles.filter(file => 
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );
    
    if (pdfFiles.length === 0) {
      setMessage("Please select PDF files only");
      setFiles([]);
      return;
    }
    
    if (pdfFiles.length < selectedFiles.length) {
      setMessage(`Warning: ${selectedFiles.length - pdfFiles.length} non-PDF file(s) were ignored. Only PDF files will be uploaded.`);
    }
    
    setFiles(pdfFiles);
    setMessage("");
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!files.length) {
      setMessage("Please select PDF files to upload");
      return;
    }

    if (files.length > 200) {
      setMessage("Cannot upload more than 200 files at once");
      return;
    }

    try {
      setUploading(true);
      setMessage("");
      setUploadedBundle(null);
      setUploadProgress(0);
      setUploadedFilesCount(0);

      const formData = new FormData();
      
      files.forEach((file) => {
        formData.append("images", file);
      });

      // Create XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      
      // Track upload progress
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);
          
          // Calculate approximate files uploaded based on progress
          const estimatedFiles = Math.round((percentComplete / 100) * files.length);
          setUploadedFilesCount(estimatedFiles);
        }
      });

      // Handle response
      const promise = new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result = JSON.parse(xhr.responseText);
              resolve(result);
            } catch (e) {
              reject(new Error("Failed to parse response"));
            }
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => reject(new Error("Network error occurred"));
        xhr.onabort = () => reject(new Error("Upload aborted"));
      });

      xhr.open("POST", `${process.env.REACT_APP_ADMIN_API_URL}/upload-bundles`,{
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`
            }});
      xhr.send(formData);

      const result = await promise;

      setUploadProgress(100);
      setUploadedFilesCount(files.length);

      setMessage(`${files.length} PDF file(s) uploaded successfully as bundle: ${result.bundleName}`);
      setUploadedBundle(result);
      setFiles([]);
      document.getElementById("pdfFileInput").value = "";
      
      fetchErrorBundles();
      
      // Reset progress after 3 seconds
      setTimeout(() => {
        setUploadProgress(0);
        setUploadedFilesCount(0);
      }, 3000);
      
    } catch (err) {
      console.error("Upload Error:", err);
      setMessage(err.message || "Something went wrong");
      setUploadProgress(0);
      setUploadedFilesCount(0);
    } finally {
      setUploading(false);
    }
  };

  // New function to handle resolve and upload
  const handleResolveAndUpload = async (bundleId, bundleName) => {
    if (!files.length) {
      setMessage("Please select PDF files to upload first");
      return;
    }

    if (files.length > 200) {
      setMessage("Cannot upload more than 200 files at once");
      return;
    }

    const confirmResolve = window.confirm(
      `Are you sure you want to resolve this error bundle and upload new files?\n\n` +
      `This will:\n` +
      `1. Delete the error bundle #${bundleId} (${bundleName})\n` +
      `2. Upload ${files.length} new PDF file(s)\n` +
      `3. Move the data to uploaded_bundles table\n\n` +
      `Continue?`
    );

    if (!confirmResolve) return;

    try {
      setResolvingId(bundleId);
      setMessage("");
      setUploadedBundle(null);
      setUploadProgress(0);
      setUploadedFilesCount(0);

      const formData = new FormData();
      
      files.forEach((file) => {
        formData.append("images", file);
      });
      
      // Add bundle info to delete the error record
      formData.append("bundleId", bundleId);
      formData.append("bundleName", bundleName);

      // Create XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      
      // Track upload progress
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);
          
          // Calculate approximate files uploaded based on progress
          const estimatedFiles = Math.round((percentComplete / 100) * files.length);
          setUploadedFilesCount(estimatedFiles);
        }
      });

      // Handle response
      const promise = new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result = JSON.parse(xhr.responseText);
              resolve(result);
            } catch (e) {
              reject(new Error("Failed to parse response"));
            }
          } else {
            reject(new Error(`Operation failed with status ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => reject(new Error("Network error occurred"));
        xhr.onabort = () => reject(new Error("Upload aborted"));
      });

      xhr.open("POST", `${process.env.REACT_APP_ADMIN_API_URL}/resolve-and-upload`,{
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`
            }});
      xhr.send(formData);

      const result = await promise;

      setUploadProgress(100);
      setUploadedFilesCount(files.length);

      setMessage(`✅ Success! Error bundle resolved and ${files.length} file(s) uploaded.`);
      setUploadedBundle(result);
      setFiles([]);
      document.getElementById("pdfFileInput").value = "";
      
      // Refresh error bundles list (this bundle should be gone now)
      fetchErrorBundles();
      
      // Reset progress after 3 seconds
      setTimeout(() => {
        setUploadProgress(0);
        setUploadedFilesCount(0);
      }, 3000);
      
    } catch (err) {
      console.error("Resolve and Upload Error:", err);
      setMessage(err.message || "Something went wrong");
      setUploadProgress(0);
      setUploadedFilesCount(0);
    } finally {
      setResolvingId(null);
      setUploading(false);
    }
  };

  const removeFile = (indexToRemove) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  const clearAllFiles = () => {
    setFiles([]);
    document.getElementById("pdfFileInput").value = "";
    setMessage("");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: '#f59e0b', bg: '#fffbeb', text: 'Pending' },
      'resolved': { color: '#10b981', bg: '#d1fae5', text: 'Resolved' },
      'rejected': { color: '#ef4444', bg: '#fee2e2', text: 'Rejected' }
    };

    const config = statusConfig[status] || { color: '#6b7280', bg: '#f3f4f6', text: status };

    return (
      <span style={{
        padding: "4px 8px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: "bold",
        backgroundColor: config.bg,
        color: config.color,
        display: "inline-block"
      }}>
        {config.text}
      </span>
    );
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    const sortableData = [...errorBundles];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    }
    return ' ↕';
  };

  return (
    <div className="upload-page">
      <div className="upload-header">
        <div>
          <h1>Upload PDF Files</h1>
          <p>Select multiple PDF files to upload (maximum 100 files at a time).</p>
        </div>

        <button className="back-btn" onClick={() => navigate("/adminpanel")}>
          Back To Admin
        </button>
      </div>

      <div className="upload-card">
        <h2>Upload PDF Files</h2>

        <form onSubmit={handleUpload} className="upload-form">
          <div className="form-group">
            <label>Select PDF Files (up to 100)</label>
            <input
              id="pdfFileInput"
              type="file"
              multiple
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
            />
            <small style={{ display: 'block', marginTop: '5px', color: '#6b7280' }}>
              You can select multiple PDF files by holding Ctrl (or Cmd on Mac) while selecting.
              Maximum 100 files per upload. Bundle name will be auto-generated based on current date/time.
            </small>
          </div>

          {/* Progress Bar */}
          {uploading && uploadProgress > 0 && (
            <div style={{ marginTop: "20px", marginBottom: "20px" }}>
              <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                marginBottom: "8px",
                fontSize: "12px",
                color: "#6b7280"
              }}>
                <span>Uploading files...</span>
                <span>{uploadedFilesCount} / {files.length} files</span>
                <span>{uploadProgress}%</span>
              </div>
              <div style={{
                width: "100%",
                height: "8px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
                overflow: "hidden"
              }}>
                <div style={{
                  width: `${uploadProgress}%`,
                  height: "100%",
                  backgroundColor: "#3b82f6",
                  transition: "width 0.3s ease",
                  animation: "pulse 1s ease-in-out infinite"
                }}></div>
              </div>
              <div style={{
                marginTop: "8px",
                fontSize: "11px",
                color: "#9ca3af",
                textAlign: "center"
              }}>
                Please wait while your files are being uploaded...
              </div>
            </div>
          )}

          {files.length > 0 && (
            <div className="selected-files-section" style={{ 
              marginTop: '20px',
              marginBottom: '20px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                backgroundColor: '#f9fafb',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <h3 style={{ margin: 0, fontSize: '16px' }}>
                  Selected Files ({files.length})
                </h3>
                <button
                  type="button"
                  onClick={clearAllFiles}
                  disabled={uploading}
                  style={{
                    padding: '4px 12px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    opacity: uploading ? 0.5 : 1
                  }}
                >
                  Clear All
                </button>
              </div>
              <div style={{ 
                maxHeight: '300px', 
                overflowY: 'auto',
                padding: '8px'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f3f4f6' }}>
                      <th style={{ padding: '8px', textAlign: 'left' }}>#</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>File Name</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Size</th>
                      <th style={{ padding: '8px', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((file, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '8px', color: '#6b7280' }}>{index + 1}</td>
                        <td style={{ padding: '8px', fontWeight: '500' }}>{file.name}</td>
                        <td style={{ padding: '8px', color: '#6b7280' }}>
                          {(file.size / 1024).toFixed(2)} KB
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            disabled={uploading}
                            style={{
                              padding: '2px 8px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '11px',
                              cursor: uploading ? 'not-allowed' : 'pointer',
                              opacity: uploading ? 0.5 : 1
                            }}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#f9fafb',
                borderTop: '1px solid #e5e7eb',
                textAlign: 'right',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                Total Size: {(files.reduce((total, file) => total + file.size, 0) / (1024 * 1024)).toFixed(2)} MB
              </div>
            </div>
          )}

          <button 
            type="submit" 
            className="upload-btn" 
            disabled={uploading || files.length === 0}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: (uploading || files.length === 0) ? 'not-allowed' : 'pointer',
              opacity: (uploading || files.length === 0) ? 0.5 : 1,
              position: 'relative'
            }}
          >
            {uploading ? `Uploading ${uploadedFilesCount}/${files.length} files...` : `Upload ${files.length} PDF File(s)`}
          </button>
        </form>
      </div>

      {/* Error Bundles Table with Resolve Button */}
      <div className="upload-card" style={{ marginTop: "20px", overflowX: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
          <h2>Error Bundles</h2>
          <button 
            onClick={fetchErrorBundles} 
            className="refresh-btn"
            style={{
              padding: "8px 12px",
              background: "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px"
            }}
          >
            Refresh
          </button>
        </div>

        {loadingErrors ? (
          <p style={{ textAlign: "center", padding: "20px" }}>Loading error bundles...</p>
        ) : errorBundles.length === 0 ? (
          <p className="no-errors" style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
            No error bundles found
          </p>
        ) : (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr style={{ backgroundColor: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: "12px", textAlign: "left", cursor: "pointer" }} onClick={() => requestSort('id')}>
                    ID{getSortIcon('id')}
                  </th>
                  <th style={{ padding: "12px", textAlign: "left", cursor: "pointer" }} onClick={() => requestSort('bundle_name')}>
                    Bundle Name{getSortIcon('bundle_name')}
                  </th>
                  <th style={{ padding: "12px", textAlign: "left", cursor: "pointer" }} onClick={() => requestSort('worker_name')}>
                    Worker{getSortIcon('worker_name')}
                  </th>
                  <th style={{ padding: "12px", textAlign: "left", cursor: "pointer" }} onClick={() => requestSort('phone_number')}>
                    Phone{getSortIcon('phone_number')}
                  </th>
                  <th style={{ padding: "12px", textAlign: "left" }}>
                    Error Message
                  </th>
                  <th style={{ padding: "12px", textAlign: "left", cursor: "pointer" }} onClick={() => requestSort('status')}>
                    Status{getSortIcon('status')}
                  </th>
                  <th style={{ padding: "12px", textAlign: "left", cursor: "pointer" }} onClick={() => requestSort('reported_at')}>
                    Reported At{getSortIcon('reported_at')}
                  </th>
                  <th style={{ padding: "12px", textAlign: "center" }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {getSortedData().map((bundle) => (
                  <tr 
                    key={bundle.id}
                    style={{ 
                      borderBottom: "1px solid #e5e7eb",
                      backgroundColor: "white"
                    }}
                  >
                    <td style={{ padding: "12px", fontWeight: "bold", color: "#6b7280" }}>
                      #{bundle.id}
                    </td>
                    <td style={{ padding: "12px", fontWeight: "bold", color: "#1e3a8a" }}>
                      {bundle.bundle_name || 'N/A'}
                    </td>
                    <td style={{ padding: "12px" }}>
                      {bundle.worker_name || 'N/A'}
                    </td>
                    <td style={{ padding: "12px" }}>
                      {bundle.phone_number || 'N/A'}
                    </td>
                    <td style={{ padding: "12px", maxWidth: "250px" }}>
                      <div style={{ 
                        overflow: "hidden", 
                        textOverflow: "ellipsis", 
                        whiteSpace: "nowrap",
                        color: "#ef4444"
                      }}>
                        {bundle.error_message || 'Unknown error'}
                      </div>
                    </td>
                    <td style={{ padding: "12px" }}>
                      {getStatusBadge(bundle.status)}
                    </td>
                    <td style={{ padding: "12px", color: "#6b7280", fontSize: "12px" }}>
                      {formatDate(bundle.reported_at)}
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <button
                        onClick={() => handleResolveAndUpload(bundle.id, bundle.bundle_name)}
                        disabled={resolvingId === bundle.id || files.length === 0 || uploading}
                        style={{
                          padding: "6px 12px",
                          background: (resolvingId === bundle.id || uploading) ? "#9ca3af" : "#10b981",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "bold",
                          cursor: (resolvingId === bundle.id || files.length === 0 || uploading) ? "not-allowed" : "pointer",
                          opacity: (resolvingId === bundle.id || files.length === 0 || uploading) ? 0.5 : 1
                        }}
                      >
                        {resolvingId === bundle.id ? "Processing..." : "Resolve & Upload"}
                      </button>
                      {files.length === 0 && (
                        <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: "4px" }}>
                          Select files first
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            <div style={{ marginTop: "12px", color: "#6b7280", fontSize: "12px", textAlign: "right" }}>
              Total: {errorBundles.length} error bundle(s)
            </div>
          </>
        )}
      </div>

      {message && (
        <div className={`upload-message ${message.includes('Warning') || message.includes('Error') ? 'error' : 'success'}`} 
          style={{
            padding: "12px 20px",
            marginTop: "20px",
            borderRadius: "8px",
            backgroundColor: (message.includes('Warning') || message.includes('Error')) ? "#fee2e2" : "#d1fae5",
            color: (message.includes('Warning') || message.includes('Error')) ? "#991b1b" : "#065f46",
            border: `1px solid ${(message.includes('Warning') || message.includes('Error')) ? "#fecaca" : "#a7f3d0"}`
          }}
        >
          {message}
        </div>
      )}

      {uploadedBundle && (
        <div className="uploaded-files upload-card" style={{ marginTop: "20px" }}>
          <h3>Upload Successful</h3>
          <div style={{ padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px' }}>
            <p><strong>Bundle Name:</strong> {uploadedBundle.bundleName}</p>
            <p><strong>Total Files:</strong> {uploadedBundle.totalFiles}</p>
            {uploadedBundle.resolvedBundleId && (
              <p><strong>Resolved Bundle ID:</strong> #{uploadedBundle.resolvedBundleId}</p>
            )}
          </div>
          <h4 style={{ marginTop: '16px', marginBottom: '8px' }}>Uploaded Files:</h4>
          <ul style={{ maxHeight: "200px", overflowY: "auto", paddingLeft: "20px" }}>
            {uploadedBundle.files?.map((file, index) => (
              <li key={index} style={{ marginBottom: "4px" }}>{file}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Upload;